
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Message, Thread } from '@/types';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

interface SessionState {
  isInitializing: boolean;
  hasInitialized: boolean;
  initializationError: string | null;
  lastInitAttempt: number;
}

interface SessionContextType {
  getSessionState: (threadId: string) => SessionState;
  preventDuplicateInit: (threadId: string, operation: () => Promise<unknown>) => Promise<unknown>;
  markSessionInitialized: (threadId: string) => void;
  resetSessionState: (threadId: string) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessionStates, setSessionStates] = useState<Record<string, SessionState>>({});
  const operationKeysRef = useRef<Set<string>>(new Set());

  const getSessionState = useCallback((threadId: string): SessionState => {
    return sessionStates[threadId] || {
      isInitializing: false,
      hasInitialized: false,
      initializationError: null,
      lastInitAttempt: 0
    };
  }, [sessionStates]);

  const updateSessionState = useCallback((threadId: string, updates: Partial<SessionState>) => {
    setSessionStates(prev => ({
      ...prev,
      [threadId]: { ...getSessionState(threadId), ...updates }
    }));
  }, [getSessionState]);

  const preventDuplicateInit = useCallback(async (threadId: string, operation: () => Promise<unknown>) => {
    const operationKey = `${threadId}-init`;
    const currentState = getSessionState(threadId);
    const now = Date.now();

    // Prevent rapid successive attempts (within 1 second)
    if (currentState.lastInitAttempt && (now - currentState.lastInitAttempt) < 1000) {
      logger.info("Blocking rapid successive initialization attempt", { threadId });
      return null;
    }

    // Check if already initializing
    if (operationKeysRef.current.has(operationKey)) {
      logger.info("Operation already in progress, skipping duplicate", { threadId });
      return null;
    }

    // Check if already initialized
    if (currentState.hasInitialized) {
      logger.info("Session already initialized, skipping", { threadId });
      return null;
    }

    // Mark as initializing
    operationKeysRef.current.add(operationKey);
    updateSessionState(threadId, {
      isInitializing: true,
      initializationError: null,
      lastInitAttempt: now
    });

    try {
      const result = await operation();
      
      updateSessionState(threadId, {
        isInitializing: false,
        hasInitialized: true,
        initializationError: null
      });

      return result;
    } catch (error: unknown) {
      const errorMessage = (error as Error)?.message || 'Unknown error';
      
      logger.error('Session initialization failed', {
        category: 'session',
        action: 'session_init_error',
        metadata: { error: errorMessage, threadId }
      });
      updateSessionState(threadId, {
        isInitializing: false,
        initializationError: errorMessage,
        lastInitAttempt: now
      });

      toast({
        title: "Session initialization failed",
        description: errorMessage,
        variant: "destructive"
      });

      throw error;
    } finally {
      operationKeysRef.current.delete(operationKey);
    }
  }, [getSessionState, updateSessionState]);

  const markSessionInitialized = useCallback((threadId: string) => {
    updateSessionState(threadId, {
      hasInitialized: true,
      isInitializing: false,
      initializationError: null
    });
  }, [updateSessionState]);

  const resetSessionState = useCallback((threadId: string) => {
    setSessionStates(prev => {
      const newState = { ...prev };
      delete newState[threadId];
      return newState;
    });
    operationKeysRef.current.forEach(key => {
      if (key.startsWith(threadId)) {
        operationKeysRef.current.delete(key);
      }
    });
  }, []);

  const contextValue: SessionContextType = {
    getSessionState,
    preventDuplicateInit,
    markSessionInitialized,
    resetSessionState
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionManager = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionManager must be used within a SessionProvider');
  }
  return context;
};
